import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Button';
import { DataTable, DataTableMessageRow } from 'components/DataTable';
import { ManagementPagination } from 'components/ManagementFilters';
import { Pill } from 'components/ManagementCard';
import { Modal } from 'components/Modal';
import { routes } from 'constants/routes';
import { useOrgPath } from 'hooks/useOrgPath';
import { summaryManagementStrings } from 'constants/strings';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useSummaryClickedUsers } from 'hooks/useSummary';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { EOrganizationPlan } from 'types/enum';
import type { TSummaryClickedUser, TSummaryFriendStatus } from 'types/summary';

const PAGE_SIZE = 50;
const COLUMN_COUNT = 4;

const strings = summaryManagementStrings.clickedUsers;

const friendStatusTone: Record<
  TSummaryFriendStatus,
  'accent' | 'danger' | 'neutral'
> = {
  NORMAL: 'accent',
  BLOCKED: 'danger',
  NO_LINKED_CHANNEL: 'neutral',
};

type ClickedUsersModalProps = {
  dispatchId: string | null;
  open: boolean;
  onClose: () => void;
  history?: boolean;
};

function formatDateTime(iso: string): string {
  return moment(iso).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm');
}

/**
 * 配信詳細から開く「クリックしたユーザー」一覧。
 * 詳細ペインはLINEプレビュー幅(375px)に固定なので、表はモーダルで全幅に出す。
 */
export function ClickedUsersModal({
  dispatchId,
  open,
  onClose,
  history = false,
}: ClickedUsersModalProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: currentUser } = useCurrentUser();
  // プランが分かるまでは遷移させない（AutoReplyMessageのchatLinkDisabledと同じ扱い）。
  const chatDisabled =
    !currentUser || currentUser.organizationPlan === EOrganizationPlan.LIGHT;

  // 開くたびに1ページ目へ戻す。配信を替えたときも、同じ配信を開き直したときも、
  // 前回のページ位置を持ち越さない。
  useEffect(() => {
    if (open) setCurrentPage(1);
  }, [open, dispatchId]);

  const { data, loading, error } = useSummaryClickedUsers({
    dispatchId,
    currentPage,
    pageSize: PAGE_SIZE,
    enabled: open,
    history,
  });

  const rows = data?.data ?? [];
  const message = loading
    ? strings.loading
    : error
      ? error
      : rows.length === 0
        ? strings.empty
        : null;

  return (
    <Modal open={open} onClose={onClose} size="xl" mobileFullScreen>
      <div className="flex items-start justify-between gap-4">
        <Dialog.Title className="text-base font-semibold text-ink">
          {strings.title}
          {data && (
            <span className="ml-2 text-sm font-normal text-muted">
              {strings.countLabel.replace(
                '$count',
                data.total.toLocaleString('ja-JP'),
              )}
            </span>
          )}
        </Dialog.Title>
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          {strings.close}
        </Button>
      </div>

      <div className="mt-4 max-h-[65vh] overflow-y-auto">
        <div className="table-scroll overflow-x-auto rounded-2xl border border-border/70 bg-white">
          <DataTable
            minWidthClassName="min-w-[640px]"
            headerRow={
              <tr>
                <Header label={strings.columns.name} />
                <Header label={strings.columns.links} />
                <Header label={strings.columns.totalClicks} />
                <Header label={strings.columns.lastClickedAt} />
              </tr>
            }
          >
            {message !== null ? (
              <DataTableMessageRow colSpan={COLUMN_COUNT} message={message} />
            ) : (
              rows.map((row) => (
                <ClickedUserRow
                  key={row.dispatchRecipientId}
                  row={row}
                  chatDisabled={chatDisabled}
                />
              ))
            )}
          </DataTable>
        </div>
      </div>

      {data && data.totalPage > 1 && (
        <div className="mt-4 flex justify-end">
          <ManagementPagination
            currentPage={currentPage}
            totalPage={data.totalPage}
            loading={loading}
            onPrevious={() => setCurrentPage((page) => Math.max(1, page - 1))}
            onNext={() => setCurrentPage((page) => page + 1)}
          />
        </div>
      )}
    </Modal>
  );
}

function Header({ label }: { label: string }) {
  return <th className="px-2 py-3 text-left leading-tight">{label}</th>;
}

function ClickedUserRow({
  row,
  chatDisabled,
}: {
  row: TSummaryClickedUser;
  chatDisabled: boolean;
}) {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const canOpenChat = !chatDisabled && row.chatChannelId !== null;

  return (
    <tr className="border-t border-border/60 bg-white">
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          {row.name !== null ? (
            canOpenChat ? (
              <button
                type="button"
                className="font-medium text-primary-700 underline-offset-2 hover:underline"
                title={strings.openChat}
                onClick={() =>
                  navigate(orgPathFor(`${routes.CHAT}?channel=${row.chatChannelId}`))
                }
              >
                {row.name}
              </button>
            ) : (
              <span>{row.name}</span>
            )
          ) : (
            <span className="text-muted" title={strings.unknownUserHint}>
              {strings.unknownUser}
            </span>
          )}
          {row.friendStatus && row.friendStatus !== 'NORMAL' && (
            <Pill tone={friendStatusTone[row.friendStatus]}>
              {strings.friendStatus[row.friendStatus]}
            </Pill>
          )}
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <ul className="space-y-1">
          {row.links.map((link) => (
            <li key={link.linkId} className="flex items-baseline gap-2">
              <span className="min-w-0 break-all text-muted">
                {link.displayUrl}
              </span>
              <span className="shrink-0">
                {strings.linkClickCount.replace(
                  '$count',
                  link.clickCount.toLocaleString('ja-JP'),
                )}
              </span>
            </li>
          ))}
        </ul>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {strings.linkClickCount.replace(
          '$count',
          row.totalClickCount.toLocaleString('ja-JP'),
        )}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatDateTime(row.lastClickedAt)}
      </td>
    </tr>
  );
}
